import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import client from "../lib/api";
import Logo from "../components/Logo";
import {
  Truck, MapPin, Calendar, Package, CheckCircle, Warning, User, Camera,
  X, ClockCounterClockwise, ArrowClockwise, Timer, Broadcast,
} from "@phosphor-icons/react";

const STEPS = ["pending", "assigned", "arrived_pickup", "in_transit", "arrived_delivery", "delivered"];
const STEP_LABEL = {
  pending: "Booked",
  assigned: "Driver Assigned",
  arrived_pickup: "At Pickup",
  in_transit: "In Transit",
  arrived_delivery: "At Delivery",
  delivered: "Delivered",
};

export default function TrackingPage() {
  const { loadId } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewer, setViewer] = useState(null);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const r = await client.get(`/track/${loadId}`);
      setData(r.data);
      setErr(null);
    } catch {
      setErr("We couldn't find this shipment. Please double-check the link.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(() => load(true), 30000); // auto-refresh every 30s
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadId]);

  const currentStep = data ? STEPS.indexOf(data.status) : -1;
  const isCancelled = data?.status === "cancelled";
  const history = data?.status_history || [];
  const photos = data?.pod_photos || [];

  return (
    <div className="min-h-screen relative noise-bg" data-testid="tracking-page">
      <header className="relative z-10 border-b border-white/10 sticky top-0 backdrop-blur-md bg-[#0A0A0A]/85">
        <div className="max-w-4xl mx-auto px-4 sm:px-8 py-4 flex items-center justify-between gap-3">
          <Link to="/">
            <Logo />
          </Link>
          <div className="flex items-center gap-3">
            <div className="text-xs text-neutral-500 uppercase tracking-widest hidden sm:block">Live Shipment Tracking</div>
            <button
              onClick={() => load(true)}
              disabled={refreshing}
              className="p-2 rounded-full border border-white/10 text-neutral-400 hover:text-white hover:border-white/25 disabled:opacity-50 transition-colors"
              data-testid="tracking-refresh"
              aria-label="Refresh"
            >
              <ArrowClockwise size={14} className={refreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
        {loading ? (
          <div className="text-center text-neutral-500 py-24" data-testid="tracking-loading">Loading shipment...</div>
        ) : err ? (
          <div className="card-surface p-12 text-center" data-testid="tracking-error">
            <Warning size={32} weight="regular" className="mx-auto text-[#F59E0B] mb-4" />
            <h2 className="font-display text-2xl font-semibold">Shipment not found</h2>
            <p className="text-neutral-400 mt-2 text-sm">{err}</p>
            <Link to="/" className="inline-block mt-6 text-sm text-[#FF5A1F] hover:text-[#FF7038]">← Back to FreightPilot AI</Link>
          </div>
        ) : (
          <>
            <div className="mb-10">
              <div className="overline mb-3">CARRIER · {data.carrier_name}</div>
              <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
                Load <span className="text-[#FF5A1F]">#{data.load_number}</span>
              </h1>
              <p className="text-neutral-400 mt-4 text-base sm:text-lg">
                {data.origin} <span className="text-neutral-600">→</span> {data.destination}
              </p>
              {data.updated_at && (
                <p className="text-xs text-neutral-600 mt-2 font-mono">
                  Last update: {new Date(data.updated_at).toLocaleString()}
                </p>
              )}
            </div>

            {/* Predicted ETA — surfaces when the load is moving and we have
                live GPS data. Auto-hides otherwise (pending/delivered loads
                or when no ELD data is available). */}
            {data.predicted_eta?.eta_iso && (
              <PredictedEtaCard eta={data.predicted_eta} status={data.status} />
            )}

            {/* Progress bar */}
            <div className="card-surface p-6 sm:p-8 mb-6" data-testid="tracking-progress">
              <div className="overline mb-6">SHIPMENT STATUS</div>
              {isCancelled ? (
                <div className="p-6 rounded-md border border-red-500/25 bg-red-500/5 text-center">
                  <div className="font-display text-2xl font-semibold text-red-300">Cancelled</div>
                  <p className="text-sm text-neutral-400 mt-2">This shipment has been cancelled. Contact the carrier for details.</p>
                </div>
              ) : (
                <>
                  {/* Desktop horizontal timeline */}
                  <div className="hidden md:block relative">
                    <div className="absolute top-5 left-5 right-5 h-0.5 bg-white/10" />
                    <div
                      className="absolute top-5 left-5 h-0.5 bg-[#FF5A1F] transition-all duration-500"
                      style={{ width: `calc((${Math.max(0, currentStep)} / ${STEPS.length - 1}) * (100% - 40px))` }}
                    />
                    <div className="relative grid grid-cols-6 gap-2">
                      {STEPS.map((step, i) => {
                        const done = i <= currentStep;
                        const current = i === currentStep;
                        return (
                          <div key={step} className="text-center" data-testid={`step-${step}`}>
                            <div
                              className={`w-10 h-10 rounded-full mx-auto flex items-center justify-center border-2 relative z-10 transition-all ${
                                done
                                  ? "bg-[#FF5A1F] border-[#FF5A1F] text-black"
                                  : "bg-[#141414] border-white/15 text-neutral-500"
                              } ${current ? "ring-4 ring-[#FF5A1F]/25 scale-110" : ""}`}
                            >
                              {done ? <CheckCircle size={16} weight="fill" /> : <span className="text-xs font-mono">{i + 1}</span>}
                            </div>
                            <div className={`text-[10px] sm:text-xs mt-3 font-medium ${done ? "text-white" : "text-neutral-500"}`}>
                              {STEP_LABEL[step]}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Mobile vertical timeline */}
                  <div className="md:hidden">
                    {STEPS.map((step, i) => {
                      const done = i <= currentStep;
                      const current = i === currentStep;
                      return (
                        <div key={step} className="flex items-start gap-3 pb-4 relative" data-testid={`step-mobile-${step}`}>
                          {i < STEPS.length - 1 && (
                            <div className={`absolute left-[19px] top-10 bottom-0 w-0.5 ${done ? "bg-[#FF5A1F]" : "bg-white/10"}`} />
                          )}
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center border-2 shrink-0 relative z-10 transition-all ${
                              done
                                ? "bg-[#FF5A1F] border-[#FF5A1F] text-black"
                                : "bg-[#141414] border-white/15 text-neutral-500"
                            } ${current ? "ring-4 ring-[#FF5A1F]/25" : ""}`}
                          >
                            {done ? <CheckCircle size={16} weight="fill" /> : <span className="text-xs font-mono">{i + 1}</span>}
                          </div>
                          <div className="pt-2">
                            <div className={`text-sm font-medium ${done ? "text-white" : "text-neutral-500"}`}>
                              {STEP_LABEL[step]}
                            </div>
                            {current && !isCancelled && (
                              <div className="text-[10px] text-[#FF5A1F] uppercase tracking-widest mt-0.5">Current</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Driver info */}
            {(data.driver_name || data.driver_truck) && (
              <div className="card-surface p-5 sm:p-6 mb-6" data-testid="tracking-driver">
                <div className="overline mb-3 flex items-center gap-2">
                  <User size={12} className="text-[#FF5A1F]" /> ASSIGNED DRIVER
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF5A1F] to-[#141414] flex items-center justify-center shrink-0">
                    <span className="font-display font-semibold text-white">
                      {(data.driver_name || "?").split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-white">{data.driver_name || "—"}</div>
                    {data.driver_truck && (
                      <div className="text-xs text-neutral-500 flex items-center gap-1 mt-0.5">
                        <Truck size={11} /> Truck #{data.driver_truck}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* POD photos */}
            {photos.length > 0 && (
              <div className="card-surface p-5 sm:p-6 mb-6" data-testid="tracking-photos">
                <div className="overline mb-4 flex items-center gap-2">
                  <Camera size={12} className="text-[#FF5A1F]" /> PROOF PHOTOS · {photos.length}
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {photos.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setViewer(p)}
                      className="aspect-square rounded-lg overflow-hidden bg-[#141414] border border-white/10 hover:border-[#FF5A1F] transition-colors"
                      data-testid={`tracking-photo-${p.id}`}
                    >
                      <img src={p.data_url} alt="Proof of delivery" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Activity timeline */}
            {history.length > 0 && (
              <div className="card-surface p-5 sm:p-6 mb-6" data-testid="tracking-timeline">
                <div className="overline mb-4 flex items-center gap-2">
                  <ClockCounterClockwise size={12} className="text-[#FF5A1F]" /> ACTIVITY · {history.length} UPDATES
                </div>
                <ol className="space-y-4">
                  {history.slice().reverse().map((h, i) => (
                    <li key={i} className="flex gap-3 relative" data-testid={`tracking-history-${i}`}>
                      {i < history.length - 1 && (
                        <div className="absolute left-[7px] top-4 bottom-0 w-px bg-white/10" />
                      )}
                      <div className="w-3.5 h-3.5 rounded-full bg-[#FF5A1F] mt-1.5 shrink-0 relative z-10" />
                      <div className="flex-1 pb-2">
                        <div className="text-sm font-medium text-white">{STEP_LABEL[h.status] || h.status}</div>
                        {h.note && <div className="text-sm text-neutral-400 mt-1 italic">"{h.note}"</div>}
                        <div className="text-xs text-neutral-600 mt-1 font-mono">
                          {new Date(h.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                          {h.driver_name && ` · by ${h.driver_name}`}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <InfoCard icon={<Calendar size={16} />} label="PICKUP" value={data.pickup_date} />
              <InfoCard icon={<Calendar size={16} />} label="DELIVERY" value={data.delivery_date} />
              <InfoCard icon={<Package size={16} />} label="CUSTOMER" value={data.customer} />
              <InfoCard icon={<Truck size={16} />} label="LAST UPDATE" value={
                data.updated_at ? new Date(data.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"
              } />
            </div>

            {data.notes && (
              <div className="card-surface p-6 mt-4">
                <div className="overline mb-2">NOTES</div>
                <p className="text-sm text-neutral-200 leading-relaxed whitespace-pre-wrap">{data.notes}</p>
              </div>
            )}
          </>
        )}

        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <p className="text-xs text-neutral-500">
            Live tracking powered by <Link to="/" className="text-[#FF5A1F] hover:text-[#FF7038]">FreightPilot AI</Link> · Auto-refreshes every 30 seconds · Questions? Contact the carrier directly.
          </p>
        </div>
      </main>

      {/* Photo lightbox */}
      {viewer && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex flex-col p-4"
          onClick={() => setViewer(null)}
          data-testid="tracking-photo-viewer"
        >
          <div className="flex justify-end mb-4">
            <button
              onClick={() => setViewer(null)}
              className="p-2 rounded-full bg-white/10 text-white hover:bg-white/20"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center">
            <img
              src={viewer.data_url}
              alt="Proof of delivery"
              className="max-w-full max-h-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="text-center text-xs text-neutral-500 mt-3">
            Uploaded {viewer.uploaded_at ? new Date(viewer.uploaded_at).toLocaleString() : ""}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * ConfidenceBadge — High/Med/Low pill sitting next to the ETA countdown.
 *
 * Confidence is computed server-side (see `core/eta_predictor.score_confidence`)
 * from GPS freshness + remaining distance + driver's 30-day on-time rate.
 * We DON'T render for `null` — pilots without any signal shouldn't see
 * a misleading "MED" default; we just hide the badge entirely.
 */
function ConfidenceBadge({ level }) {
  if (!level) return null;
  const cfg = {
    high: {
      label: "HIGH CONFIDENCE",
      cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
      dot: "bg-emerald-500",
    },
    med: {
      label: "MED CONFIDENCE",
      cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",
      dot: "bg-amber-400",
    },
    low: {
      label: "LOW CONFIDENCE",
      cls: "bg-red-500/15 text-red-300 border-red-500/40",
      dot: "bg-red-400",
    },
  }[level] || {
    label: level.toUpperCase(),
    cls: "bg-neutral-500/15 text-neutral-400 border-neutral-500/40",
    dot: "bg-neutral-500",
  };
  return (
    <div
      data-testid={`confidence-badge-${level}`}
      className={`shrink-0 hidden sm:inline-flex items-center gap-1.5 border rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${cfg.cls}`}
    >
      <span className={`inline-block w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </div>
  );
}

function InfoCard({ icon, label, value }) {
  return (
    <div className="card-surface p-4 sm:p-5">
      <div className="flex items-center gap-2 text-neutral-500 mb-2">
        <span className="text-[#FF5A1F]">{icon}</span>
        <span className="overline">{label}</span>
      </div>
      <div className="text-sm text-neutral-100 font-medium break-words">{value || "—"}</div>
    </div>
  );
}

/**
 * PredictedEtaCard — the "Predicted arrival" hero card on the public
 * tracking page. Renders only when the load is in-motion and we have
 * live GPS data (backend serves `predicted_eta` conditionally).
 *
 * The card is intentionally the most visually prominent element on the
 * page — a delayed shipment's #1 customer question is "when will it
 * arrive?" and answering that above the fold reduces support tickets.
 */
function PredictedEtaCard({ eta, status }) {
  const etaDate = new Date(eta.eta_iso);
  const mins = eta.minutes_remaining ?? 0;
  const isStale = eta.freshness === "stale";
  const isArrived = mins <= 0 || status === "arrived_delivery";

  const humanCountdown = (() => {
    if (isArrived) return "Arriving now";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  })();

  return (
    <div
      className="card-surface p-6 sm:p-7 mb-6 relative overflow-hidden"
      data-testid="tracking-eta-card"
    >
      {/* Subtle live-signal pulse in the corner */}
      <div className="absolute top-4 right-4 flex items-center gap-1.5">
        <span className="relative inline-flex h-2 w-2">
          {!isStale && (
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
          )}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${
            isStale ? "bg-amber-400" : "bg-emerald-500"
          }`} />
        </span>
        <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-mono">
          {isStale ? "GPS stale" : "Live"}
        </span>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <Timer size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">
            Predicted arrival
          </div>
          <div className="font-display text-2xl sm:text-3xl font-semibold text-white leading-tight mt-0.5">
            {humanCountdown}
          </div>
        </div>
        <ConfidenceBadge level={eta.confidence} />
      </div>

      <div className="grid grid-cols-2 gap-3 sm:gap-4">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
          <div className="overline mb-1">ETA</div>
          <div className="text-sm font-medium text-white tabular-nums">
            {etaDate.toLocaleString([], {
              weekday: "short", hour: "numeric", minute: "2-digit",
            })}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 p-3">
          <div className="overline mb-1 flex items-center gap-1.5">
            <Broadcast size={9} /> Distance to go
          </div>
          <div className="text-sm font-medium text-white tabular-nums">
            {(eta.distance_mi || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} mi
          </div>
        </div>
      </div>

      {isStale && (
        <div className="text-[11px] text-amber-300/80 mt-3 leading-relaxed">
          Note: the last GPS ping is older than 20 minutes — ETA may drift as we get a fresh update.
        </div>
      )}
    </div>
  );
}
